import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import PineMark from "@/components/PineMark";

// Primary navigation (always visible on desktop).
const primaryNav = [
  { to: "/systeme", label: "Systeme" },
  { to: "/gruenden", label: "Gründen" },
  { to: "/cooperations", label: "Cooperations" },
];

// Items bundled under "Mehr" dropdown on desktop, expanded on mobile.
const moreNav = [
  { to: "/insights", label: "Insights" },
  { to: "/ablauf", label: "Ablauf" },
  { to: "/faq", label: "FAQ" },
  { to: "/kontakt", label: "Kontakt" },
];

// Full mobile order (does NOT include "Standort prüfen" — that lives only as CTA).
const mobileNav = [...primaryNav, ...moreNav];

function Logo({ size = 22 }) {
  return (
    <Link to="/" className="flex items-center gap-3 leading-none group" data-testid="pine-logo">
      <PineMark size={size} className="text-[#18E096] transition-colors group-hover:text-white" />
      <span className="flex flex-col">
        <span className="font-display text-white text-base tracking-[0.2em]">PINE</span>
        <span className="font-display text-white text-base tracking-[0.2em] -mt-0.5">SYSTEMS</span>
      </span>
    </Link>
  );
}

function MoreDropdown() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const moreActive = moreNav.some((it) => location.pathname.startsWith(it.to));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-more"
        className={`inline-flex items-center gap-1.5 text-sm uppercase tracking-[0.18em] transition-colors ${
          moreActive ? "text-[#18E096]" : "text-[#AEB7B7] hover:text-white"
        }`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        Mehr
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="nav-more-dropdown"
          className="absolute right-0 mt-3 min-w-[200px] border border-[rgba(255,255,255,0.10)] bg-[#0B0F10] shadow-2xl"
        >
          {moreNav.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              role="menuitem"
              data-testid={`nav-more-${it.to.replace("/", "")}`}
              className={({ isActive }) =>
                `block px-5 py-3 text-xs uppercase tracking-[0.18em] border-b border-[rgba(255,255,255,0.06)] last:border-b-0 transition-colors ${
                  isActive
                    ? "text-[#18E096] bg-[#141819]"
                    : "text-[#AEB7B7] hover:text-white hover:bg-[#141819]"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname, setOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-[#050607] text-[#F5F7F7]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[rgba(255,255,255,0.08)] bg-[rgba(5,6,7,0.8)] backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {primaryNav.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "text-[#18E096]" : "text-[#AEB7B7] hover:text-white"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <MoreDropdown />
          </nav>
          <div className="hidden lg:block">
            <Link
              to="/standort-pruefen"
              className="inline-flex items-center gap-1.5 whitespace-nowrap bg-[#18E096] text-[#050607] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] border border-[#18E096] hover:bg-transparent hover:text-[#18E096] transition-colors"
              data-testid="header-cta"
            >
              Standort prüfen <ArrowUpRight size={14} strokeWidth={1.75} />
            </Link>
          </div>
          <button
            className="lg:hidden p-2 border border-[rgba(255,255,255,0.12)]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[rgba(255,255,255,0.08)] bg-[#050607]">
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
              {mobileNav.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`mobile-nav-${it.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `text-base uppercase tracking-[0.18em] py-2 ${
                      isActive ? "text-[#18E096]" : "text-white"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              ))}
              <Link
                to="/standort-pruefen"
                className="btn-primary mt-2 justify-center"
                data-testid="mobile-cta"
              >
                Standort prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-[rgba(255,255,255,0.08)] mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <Logo />
            <p className="text-[#AEB7B7] mt-6 max-w-md text-sm leading-relaxed">
              PINE Systems entwickelt Systemlösungen für moderne Erlebnisstandorte
              und digitale Markenräume. Aus Flächen werden Erlebnisse. Aus Erlebnissen wird System.
            </p>
          </div>
          <div>
            <div className="overline mb-4">Systeme</div>
            <ul className="space-y-2 text-sm text-[#AEB7B7]">
              <li><Link to="/pvra" className="hover:text-white">PVRA</Link></li>
              <li><Link to="/ar-ena" className="hover:text-white">AR_ENA</Link></li>
              <li><Link to="/pine-vr" className="hover:text-white">PINE VR</Link></li>
              <li><Link to="/hotel-vr" className="hover:text-white">Hotel VR</Link></li>
              <li><Link to="/pine-places" className="hover:text-white" data-testid="footer-pine-places">PINE Places</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-4">Mehr</div>
            <ul className="space-y-2 text-sm text-[#AEB7B7]">
              <li><Link to="/gruenden" className="hover:text-white">Gründen</Link></li>
              <li><Link to="/insights" className="hover:text-white">Insights</Link></li>
              <li><Link to="/cooperations" className="hover:text-white">Cooperations</Link></li>
              <li><Link to="/ablauf" className="hover:text-white">Ablauf</Link></li>
              <li><Link to="/standort-pruefen" className="hover:text-[#18E096]">Standort prüfen</Link></li>
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              <li><Link to="/kontakt" className="hover:text-white">Kontakt</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[rgba(255,255,255,0.08)]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#AEB7B7] tracking-wider uppercase">
            <span>© {new Date().getFullYear()} PINE Systems. Alle Rechte vorbehalten.</span>
            <div className="flex flex-wrap items-center gap-5">
              <Link to="/impressum" className="hover:text-white" data-testid="footer-impressum">
                Impressum
              </Link>
              <Link to="/datenschutz" className="hover:text-white" data-testid="footer-datenschutz">
                Datenschutz
              </Link>
              <span className="opacity-70">Erlebnisstandorte · Markenräume · System</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
